import { HttpErrorResponse } from '@angular/common/http'
import {
  PositionActionKey,
  PositionActionValidation,
} from '../models/position-action.model'

/**
 * Turns a failed `POST /flex-positions/actions` into one sentence for a toast.
 *
 * The endpoint does NOT use the usual Helios ReportableError envelope for a rejection. A 400 body
 * is an ARRAY of `{ validationId, message }` (Helios's `FlexPositionValidation`), carrying a stable,
 * greppable reason code — the same fourteen codes flex-transactions returns, plus the position-only
 * `unsupportedPositionType`. That is the contract a client is meant to switch on, and the precedent
 * for reading it this way is `clearwater-transactions-task.service.ts`, which already does
 * `err?.error?.[0]?.validationId`.
 *
 * The server's own `message` is not shown. It is written for a log — it interpolates session ids,
 * raw row ids and account ids — so each code gets a rewrite here instead, and an unrecognised code
 * falls back to a generic sentence rather than leaking one.
 */

/** Per-action verb for the fallback sentence. */
const ACTION_VERB: Record<PositionActionKey, string> = {
  match: 'match these positions',
  unmatch: 'unmatch these positions',
  ignore: 'ignore these positions',
  unignore: 'unignore these positions',
}

/**
 * `validationId` -> what to tell the user.
 *
 * Every code the backend can emit is listed, so a rejection is explicable rather than generic.
 * Codes are matched EXACTLY: they are compile-time constants on
 * `FlexPositionCommandsService`, not free text, and the file header there calls them stable on
 * purpose. A code that is not in this map still surfaces, via the fallback.
 */
export const POSITION_ACTION_MESSAGES: Record<string, string> = {
  // Shape of the selection.
  bothSidesRequired:
    'A match needs at least one Enfusion position and one Prometheus position.',
  idsRequired: 'Select at least one position first.',
  rowsNotFound:
    'Some of the selected positions are no longer in this session. Reload the panel and try again.',
  accountNotInSession:
    'Some of the selected positions belong to an account that is not loaded in this session.',
  crossAccountMatch:
    'A match group covers a single account, but the selected positions span several.',

  // Prometheus feed resolution.
  missingDataSourceType:
    'A selected Prometheus position has no data source, so it cannot be matched. Re-run the session and try again.',
  unknownDataSourceType:
    'A selected Prometheus position has an unrecognised data source, so it cannot be matched.',
  promSourcesSpan:
    'The selected Prometheus positions span both Custody and Trading. Select positions from one of them — the other can be added once the match exists, after the next Run.',

  // Group coherence.
  alreadyMatched: 'Every selected position is already in this match group.',
  cannotMergeGroups:
    'The two sides belong to different match groups, and merging groups is not supported.',
  sideSpansMultipleGroups:
    'The selected positions belong to more than one match group, and merging groups is not supported.',
  notMatched: 'None of the selected positions are matched.',
  incoherentMembers:
    'A match group needs at least two positions from at least two different sources.',

  // Position-only.
  unsupportedPositionType:
    'Only Enfusion position rows can be matched — cash balances cannot.',
}

/** The first validation entry on a 400, or null when the failure carried no such body. */
export function firstValidation(err: unknown): PositionActionValidation | null {
  if (!(err instanceof HttpErrorResponse)) return null
  const body = err.error as unknown
  if (!Array.isArray(body) || !body.length) return null
  const first = body[0] as Partial<PositionActionValidation>
  return typeof first?.validationId === 'string'
    ? { validationId: first.validationId, message: first.message ?? '' }
    : null
}

/**
 * The sentence to show for a failed action.
 *
 * A transport failure (no response body, a 5xx, a 404 while the endpoint is undeployed) has no
 * validationId and lands on the fallback, which is deliberately the same sentence a recognised-code
 * miss gets: from the user's side both mean "it did not happen, try again".
 */
export function positionActionErrorMessage(
  action: PositionActionKey,
  err: unknown,
): string {
  const validation = firstValidation(err)
  const known = validation && POSITION_ACTION_MESSAGES[validation.validationId]
  return known ?? `Unable to ${ACTION_VERB[action]}. Please try again.`
}
